import { 
  CreateRoleServiceModel, 
  UpdateRoleServiceModel, 
  RoleServiceResponseModel,
  BaseOperationResult,
  RolePaginatedResult,
  PaginationParams,
  AuthTokens
} from '../types';
import { API_CONFIG } from '../config/api';
import { AuthenticatedApiService } from './authenticatedApiService';

class RolesApiService extends AuthenticatedApiService {
  async getRoles(params: PaginationParams = {}): Promise<RolePaginatedResult> {
    try {
      const queryParams = new URLSearchParams();
      
      if (params.page !== undefined) {
        queryParams.append('page', params.page.toString());
      }
      if (params.pageSize !== undefined) {
        queryParams.append('pageSize', params.pageSize.toString());
      }
      if (params.searchTerm) {
        queryParams.append('searchTerm', params.searchTerm);
      }
      if (params.sortBy) {
        queryParams.append('sortBy', params.sortBy);
      }
      if (params.sortDirection) {
        queryParams.append('sortDirection', params.sortDirection);
      }

      const url = `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.ROLES}${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
      
      const response = await this.makeAuthenticatedCall(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      return await response.json();
    } catch (error) {
      console.error('Get roles error:', error);
      throw error;
    }
  }

  async getRoleById(id: string): Promise<BaseOperationResult<RoleServiceResponseModel>> {
    try {
      const response = await this.makeAuthenticatedCall(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.ROLE_BY_ID(id)}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      return await response.json();
    } catch (error) {
      console.error('Get role by ID error:', error);
      throw error;
    }
  }

  /**
   * Get tenants for a specific role
   */
  async getTenantsForRole(roleId: string): Promise<BaseOperationResult<{ tenant: { id: string; name: string } }[]>> {
    try {
      const response = await this.makeAuthenticatedCall(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.ROLE_BY_ID(roleId)}/tenants`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      return await response.json();
    } catch (error) {
      console.error('Get tenants for role error:', error);
      throw error;
    }
  }

  /**
   * Update tenant assignments for a specific role
   * Payload matches Swagger spec: UpdateRoleTenantsServiceModel
   * - add: array of UUID strings (nullable per spec, but we send empty arrays)
   * - remove: array of UUID strings (nullable per spec, but we send empty arrays)
   */
  async updateTenantAssignments(roleId: string, assignments: { add: string[]; remove: string[] }): Promise<BaseOperationResult<any>> {
    try {
      console.log('🔐 Updating tenant assignments for role:', roleId, assignments);
      
      const response = await this.makeAuthenticatedCall(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.ROLE_BY_ID(roleId)}/tenants`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(assignments),
      });

      return await response.json();
    } catch (error) {
      console.error('Update tenant assignments error:', error);
      throw error;
    }
  }

  /**
   * Get permissions for a specific role
   */
  async getPermissionsForRole(roleId: string): Promise<BaseOperationResult<{ id: string; name: string; description: string }[]>> {
    try {
      const queryParams = new URLSearchParams();
      
      queryParams.append('pageNumber', '1');
      queryParams.append('pageSize', '100');
      
      const response = await this.makeAuthenticatedCall(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.ROLE_BY_ID(roleId)}/permissions?${queryParams.toString()}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      return await response.json();
    } catch (error) {
      console.error('Get permissions for role error:', error);
      throw error;
    }
  }

  /**
   * Update permission assignments for a specific role
   */
  async updatePermissionAssignments(roleId: string, assignments: { add: string[]; remove: string[] }): Promise<BaseOperationResult<any>> {
    try {
      console.log('🔐 Updating permission assignments for role:', roleId, assignments);
      
      const response = await this.makeAuthenticatedCall(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.ROLE_BY_ID(roleId)}/permissions`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(assignments),
      });

      return await response.json();
    } catch (error) {
      console.error('Update permission assignments error:', error);
      throw error;
    }
  }

  async createRole(role: CreateRoleServiceModel): Promise<BaseOperationResult<RoleServiceResponseModel>> {
    try {
      const response = await this.makeAuthenticatedCall(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.ROLES}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(role),
      });

      return await response.json();
    } catch (error) {
      console.error('Create role error:', error);
      throw error;
    }
  }

  async updateRole(id: string, role: UpdateRoleServiceModel): Promise<BaseOperationResult<RoleServiceResponseModel>> {
    try {
      const response = await this.makeAuthenticatedCall(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.ROLE_BY_ID(id)}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(role),
      });

      return await response.json();
    } catch (error) {
      console.error('Update role error:', error);
      throw error;
    }
  }

  async deleteRole(id: string): Promise<BaseOperationResult<boolean>> {
    try {
      const response = await this.makeAuthenticatedCall(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.ROLE_BY_ID(id)}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      return await response.json();
    } catch (error) {
      console.error('Delete role error:', error);
      throw error;
    }
  }

  async toggleRoleStatus(id: string, isActive: boolean): Promise<BaseOperationResult<RoleServiceResponseModel>> {
    try {
      return await this.updateRole(id, { isActive });
    } catch (error) {
      console.error('Toggle role status error:', error);
      throw error;
    }
  }
}

const rolesApiService = new RolesApiService();

export const rolesService = {
  initialize(tokens: AuthTokens | null, onTokenRefresh: (tokens: AuthTokens) => void) {
    rolesApiService.setTokens(tokens, onTokenRefresh);
  },
  
  async getRoles(params: PaginationParams = {}): Promise<RolePaginatedResult> {
    return rolesApiService.getRoles(params);
  },

  async getRoleById(id: string): Promise<BaseOperationResult<RoleServiceResponseModel>> {
    return rolesApiService.getRoleById(id);
  },

  async getTenantsForRole(roleId: string): Promise<BaseOperationResult<{ tenant: { id: string; name: string } }[]>> {
    return rolesApiService.getTenantsForRole(roleId);
  },

  async updateTenantAssignments(roleId: string, assignments: { add: string[]; remove: string[] }): Promise<BaseOperationResult<any>> {
    return rolesApiService.updateTenantAssignments(roleId, assignments);
  },

  async getPermissionsForRole(roleId: string): Promise<BaseOperationResult<{ id: string; name: string; description: string }[]>> {
    return rolesApiService.getPermissionsForRole(roleId);
  },

  async updatePermissionAssignments(roleId: string, assignments: { add: string[]; remove: string[] }): Promise<BaseOperationResult<any>> {
    return rolesApiService.updatePermissionAssignments(roleId, assignments);
  },

  async createRole(role: CreateRoleServiceModel): Promise<BaseOperationResult<RoleServiceResponseModel>> {
    return rolesApiService.createRole(role);
  },

  async updateRole(id: string, role: UpdateRoleServiceModel): Promise<BaseOperationResult<RoleServiceResponseModel>> {
    return rolesApiService.updateRole(id, role);
  },

  async deleteRole(id: string): Promise<BaseOperationResult<boolean>> {
    return rolesApiService.deleteRole(id);
  },

  async toggleRoleStatus(id: string, isActive: boolean): Promise<BaseOperationResult<RoleServiceResponseModel>> {
    return rolesApiService.toggleRoleStatus(id, isActive);
  },

  isAuthenticated(): boolean {
    return rolesApiService.isAuthenticated();
  }
};
